import React, { FC, useCallback, useEffect, useState } from 'react'
import { Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { IBankAccount, CreateUserBankAccountRequestDto, UpdateBankAccountRequestDto } from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { InputSelect } from '../../../../../../../components/Dashboard/InputSelect'
import { createEnum } from '../../../../../../../utils/createEnum'
import { InputField } from '../../../../../../../components/Dashboard/InputField'
import { Modal } from '../../../../../../../components/Dashboard/Modal'
import { createUserBankAccountRequest, updateUserBankAccountRequest } from '../../../../../../../store/actions'
import { fiatsSelector } from '../../../../../../../store/selectors'
// import { createSfBankAccountRequest, updateSfBankAccountRequest } from '../../../../store/actions'

interface Props {
  isOpen: boolean
  isSubmit: boolean
  modalClose: () => void
  fields: IBankAccount
  title: string
  shouldFieldsBeDisabled?: boolean
}

export const BankAccountsModal: FC<Props> = ({
  isOpen,
  isSubmit,
  fields,
  modalClose,
  title,
  shouldFieldsBeDisabled,
}) => {
  const [formValues, setFormValues] = useState<IBankAccount>(fields || {})
  const dispatch = useDispatch()
  const fiats = useSelector(fiatsSelector)

  const formMethods = useForm<IBankAccount>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(fields ? UpdateBankAccountRequestDto : CreateUserBankAccountRequestDto),
  })

  const {
    handleSubmit,
    formState: { isValid },
    getValues,
    setValue,
    reset,
  } = formMethods

  useEffect(() => {
    if (!!fields && Object.keys(fields).length > 0) {
      setFormValues((prev) => {
        if (Object.keys(prev).length === 0) {
          return fields
        }

        return prev
      })
    }
  }, [fields])

  useEffect(() => {
    reset()
    Object.entries(formValues).forEach(([key, value]) => {
      setValue(key as keyof IBankAccount, value)
    })
  }, [formValues, getValues, reset, setValue])

  const formSubmitHandle = useCallback(
    (data: IBankAccount) => {
      if ('id' in data) {
        dispatch(updateUserBankAccountRequest(data))
      } else {
        dispatch(createUserBankAccountRequest(data))
      }

      reset()
      modalClose?.()
    },
    [dispatch, modalClose, reset],
  )

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(formSubmitHandle)}
      onCancel={modalClose}
      submitTitle={'Submit'}
      cancelTitle={'Close'}
      isSubmit={isSubmit}
      isSubmitDisabled={!isValid}
      width={680}
      submitButtonProps={{
        type: 'button',
      }}
    >
      <FormProvider {...formMethods}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} container direction="column">
            <InputSelect
              key="currency"
              labelName="Currency"
              inputName="currency"
              placeholder="Please Select"
              enums={createEnum(fiats)}
              width={'100%'}
              mb={20}
              defaultValue={formValues?.currency}
              disable={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankName"
              labelName="Bank Name"
              inputName="bankName"
              inputType="text"
              width={'100%'}
              marginBottom={20}
              defaultValue={formValues?.bankName}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankSwift"
              labelName="Bank SWIFT/BIC"
              inputName="bankSwift"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankSwift}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankAccountNumber"
              labelName="Bank Account Number"
              inputName="bankAccountNumber"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankAccountNumber}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="bankAddress"
              labelName="Bank Address"
              inputName="bankAddress"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.bankAddress}
              disabled={shouldFieldsBeDisabled}
            />
          </Grid>

          <Grid item xs={12} sm={6} container direction="column">
            <InputField
              key="beneficiaryName"
              labelName="Beneficiary Name"
              inputName="beneficiaryName"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.beneficiaryName}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="intermediaryBankName"
              labelName="Intermediary Bank Name"
              inputName="intermediaryBankName"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.intermediaryBankName}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="intermediaryBankSwift"
              labelName="Intermediary Bank SWIFT/BIC"
              inputName="intermediaryBankSwift"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.intermediaryBankSwift}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="intermediaryBankAddress"
              labelName="Intermediary Bank Address"
              inputName="intermediaryBankAddress"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.intermediaryBankAddress}
              disabled={shouldFieldsBeDisabled}
            />
            <InputField
              key="referenceToInclude"
              labelName="Reference to Include"
              inputName="referenceToInclude"
              inputType="text"
              width={'100%'}
              defaultValue={formValues?.referenceToInclude}
              disabled={shouldFieldsBeDisabled}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </Modal>
  )
}
